import { useEffect, useState } from "react";
import { MapPin, Search } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

const useA = () => { const { lang } = useApp(); return (en, fr) => (lang === "fr" ? fr : en); };
const X = (lng) => (lng < -90 ? 40 + ((lng + 120) / 30) * 260 : 300 + ((lng + 90) / 22) * 680);
const Y = (lat) => 520 - ((lat - 42) / 14) * 480;
const LABEL = { "ottawa-gatineau": [-8, 1, "end"], montreal: [8, 1, "start"], "quebec-city": [0, -1, "middle"] };

const MetricPicker = ({ metric, setMetric, metrics }) => (
  <div className="flex flex-wrap gap-1.5" role="radiogroup">
    {metrics.map(([k, l]) => (
      <button key={k} role="radio" aria-checked={metric === k} data-testid={`geo-metric-${k}`} onClick={() => setMetric(k)}
        className={`press min-h-[40px] px-3 rounded-full text-sm font-bold ${metric === k ? "bg-[#1E4D3B] text-white" : "bg-[#F3EDE4]"}`}>{l}</button>
    ))}
  </div>
);

const GeoMap = ({ hubs, metric, sel, setSel }) => {
  const { L } = useApp();
  const max = Math.max(1, ...hubs.map((h) => h[metric]));
  return (
    <svg viewBox="0 0 1000 560" className="w-full h-auto rounded-[20px] bg-[#ECF3EF]" data-testid="geo-map" role="img" aria-label="Map">
      {[44, 48, 52, 56].map((lat) => <line key={lat} x1="0" x2="1000" y1={Y(lat)} y2={Y(lat)} stroke="#1E4D3B" strokeOpacity="0.07" />)}
      <text x="500" y="110" textAnchor="middle" fontSize="72" fontWeight="800" fill="#1E4D3B" fillOpacity="0.06" letterSpacing="24">CANADA</text>
      <path d={`M ${X(-123)} ${Y(49)} L ${X(-95)} ${Y(49)} L ${X(-89)} ${Y(48)} L ${X(-83)} ${Y(46)} L ${X(-82.5)} ${Y(42.2)} L ${X(-79)} ${Y(43.3)} L ${X(-74.7)} ${Y(45)} L ${X(-71.5)} ${Y(45)} L ${X(-69)} ${Y(47.3)}`}
        fill="none" stroke="#C2410C" strokeOpacity="0.35" strokeWidth="2" strokeDasharray="6 6" />
      <text x={X(-104)} y={Y(47.6)} fontSize="20" fill="#C2410C" fillOpacity="0.6">USA</text>
      {hubs.filter((h) => h.lat).map((h) => {
        const r = 12 + 22 * Math.sqrt(h[metric] / max);
        const active = h.launch_status === "active";
        const [dx, dy, anchor] = LABEL[h.id] || [0, 1, "middle"];
        return (
          <g key={h.id} data-testid={`geo-bubble-${h.id}`} onClick={() => setSel(h.id)} className="cursor-pointer">
            <circle cx={X(h.lng)} cy={Y(h.lat)} r={r} fill={active ? "#1E4D3B" : "#C2410C"} fillOpacity={sel === h.id ? 0.9 : 0.55} stroke="#F59E0B" strokeWidth={sel === h.id ? 4 : 0} />
            <text x={X(h.lng)} y={Y(h.lat) + 8} textAnchor="middle" fontSize="22" fontWeight="800" fill="#fff">{h[metric]}</text>
            <text x={X(h.lng) + dx} y={dy > 0 ? Y(h.lat) + r + 26 : Y(h.lat) - r - 10} textAnchor={anchor} fontSize="24" fontWeight="700" fill="#291E18">{L(h)}</text>
          </g>
        );
      })}
    </svg>
  );
};

const Bar = ({ label, value, max, color = "#1E4D3B" }) => (
  <div className="text-sm">
    <div className="flex justify-between gap-2"><span className="truncate">{label}</span><b>{value}</b></div>
    <div className="h-2 rounded-full bg-[#F3EDE4] mt-1"><div className="h-2 rounded-full" style={{ width: `${value ? Math.max(3, (100 * value) / Math.max(1, max)) : 0}%`, background: color }} /></div>
  </div>
);

const HubDetail = ({ h }) => {
  const a = useA(); const { L } = useApp();
  const maxA = Math.max(1, ...h.areas.map((x) => x.posts + x.users));
  const cell = (label, v) => <div className="rounded-2xl bg-[#FFFDF9] border p-3"><div className="text-xs text-[#6B5E55] font-semibold">{label}</div><div className="text-xl font-extrabold text-[#1E4D3B]">{v}</div></div>;
  return (
    <div className="space-y-4" data-testid={`geo-detail-${h.id}`}>
      <h3 className="text-xl font-extrabold inline-flex items-center gap-2"><MapPin className="w-5 h-5 text-[#C2410C]" />{L(h)} <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-[#F3EDE4]">{h.launch_status === "active" ? a("Active", "Actif") : a("Soon", "Bientôt")}</span></h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cell(a("Members", "Membres"), h.users)}{cell(a("Verified", "Vérifiés"), h.verified)}{cell(a("New members (period)", "Nouveaux membres (période)"), h.new_users ?? "—")}{cell(a("Real posts", "Publications réelles"), h.posts)}
        {cell(a("Listings / Requests", "Annonces / Demandes"), `${h.listings} / ${h.requests}`)}{cell(a("Post views", "Vues"), h.views)}{cell(a("WhatsApp contacts", "Contacts WhatsApp"), h.contacts)}{cell(a("Waitlist", "Liste d'attente"), h.waitlist)}
        {cell(a("Searches", "Recherches"), h.searches)}{cell(a("City selections", "Sélections de ville"), h.hub_selections)}{cell(a("Detected by location", "Détectées par localisation"), h.gps_detections)}
      </div>
      {Object.keys(h.sides).length > 0 && (
        <div className="grid grid-cols-2 gap-2" data-testid="geo-sides">
          {Object.entries(h.sides).map(([s, v]) => <div key={s} className="rounded-2xl bg-[#ECF3EF] p-3"><div className="font-extrabold">{s === "ottawa" ? a("Ottawa side", "Côté Ottawa") : a("Gatineau side", "Côté Gatineau")}</div><div className="text-sm">{v.posts} {a("posts", "publications")} · {v.users} {a("members", "membres")}</div></div>)}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2" data-testid="geo-areas"><div className="font-bold">{a("Top areas (posts + members)", "Secteurs principaux (publications + membres)")}</div>
          {h.areas.length === 0 ? <p className="text-sm text-[#6B5E55]">{a("No area data yet.", "Aucune donnée de secteur.")}</p> : h.areas.slice(0, 10).map((x) => <Bar key={x.id} label={`${L(x)} · ${x.posts}p / ${x.users}m`} value={x.posts + x.users} max={maxA} />)}
        </div>
        <div className="space-y-2" data-testid="geo-searches"><div className="font-bold inline-flex items-center gap-1"><Search className="w-4 h-4" />{a("Top searches", "Recherches populaires")}</div>
          {h.top_searches.length === 0 ? <p className="text-sm text-[#6B5E55]">{a("No searches yet.", "Aucune recherche.")}</p> : h.top_searches.map((s) => <Bar key={s.q} label={`“${s.q}”`} value={s.n} max={h.top_searches[0].n} color="#D97706" />)}
        </div>
      </div>
    </div>
  );
};

export const Geography = () => {
  const a = useA(); const { L } = useApp();
  const [q, setQ] = useState({ date_from: "", date_to: "" });
  const [d, setD] = useState(null);
  const [metric, setMetric] = useState("users");
  const [sel, setSel] = useState("montreal");
  useEffect(() => { const p = {}; Object.entries(q).forEach(([k, v]) => v && (p[k] = v)); api.get("/admin/geo", { params: p }).then((r) => setD(r.data)); }, [q]);
  const metrics = [["users", a("Members", "Membres")], ["posts", a("Posts", "Publications")], ["contacts", a("Contacts", "Contacts")], ["views", a("Views", "Vues")], ["searches", a("Searches", "Recherches")], ["waitlist", a("Waitlist", "Attente")]];
  if (!d) return <div className="skeleton h-72 rounded-[20px]" />;
  const h = d.hubs.find((x) => x.id === sel) || d.hubs[0];
  const max = Math.max(1, ...d.hubs.map((x) => x[metric]));
  return (
    <div className="space-y-5" data-testid="admin-geo">
      <div className="flex flex-wrap gap-2 items-center">
        <input data-testid="geo-from" type="date" value={q.date_from} onChange={(e) => setQ({ ...q, date_from: e.target.value })} className="min-h-[44px] rounded-full border px-3 bg-[#FFFDF9]" />
        <input data-testid="geo-to" type="date" value={q.date_to} onChange={(e) => setQ({ ...q, date_to: e.target.value })} className="min-h-[44px] rounded-full border px-3 bg-[#FFFDF9]" />
        <span className="text-xs text-[#6B5E55]">{a("Demo/sample data excluded.", "Données exemples exclues.")}</span>
      </div>
      <MetricPicker metric={metric} setMetric={setMetric} metrics={metrics} />
      <GeoMap hubs={d.hubs} metric={metric} sel={sel} setSel={setSel} />
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="geo-ranking">
        {[...d.hubs].sort((x, y) => y[metric] - x[metric]).map((x) => (
          <button key={x.id} onClick={() => setSel(x.id)} className="text-left press rounded-xl p-1"><Bar label={L(x)} value={x[metric]} max={max} color={x.launch_status === "active" ? "#1E4D3B" : "#C2410C"} /></button>
        ))}
      </div>
      {h && <HubDetail h={h} />}
    </div>
  );
};
